import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Avatar } from '@/components/Avatar';
import { Input } from '@/components/Controls/Input';
import { Textarea } from '@/components/Controls/Textarea';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { useEffect } from 'react';
import { FieldValues, Path, PathValue } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { getTranslatedItem } from '@/utils/language.utils';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { useGetExistingDamage } from '@/hooks/damage.hooks';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { FieldError } from '@/type/error.types';
import { Select } from '@/components/Controls/Select';

export const DamageInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const translatedDamage = getTranslatedItem(data?.translatedDamages);

  /**
   * === Set Value Watchers section ===
   */
  const nameValue = watch && watch('name' as Path<TSchema>);
  const severityValue = watch && watch('severity' as Path<TSchema>);
  const activeValue = watch && watch('active' as Path<TSchema>);
  /**
   * === END OF Set Value Watchers section ===
   */

  const { data: existingDamage } = useGetExistingDamage({
    id: data?.id,
    name: nameValue,
    severity: severityValue,
  });

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (nameValue) {
      resetFieldError<TSchema>({ fieldName: 'name', setError });
    }
    if (severityValue) {
      resetFieldError<TSchema>({ fieldName: 'severity', setError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameValue, severityValue]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue('icon' as Path<TSchema>, data.icon ?? '');
      setValue(
        'name' as Path<TSchema>,
        (translatedDamage?.name || data.name) ?? '',
      );
      setValue('severity' as Path<TSchema>, data.severity ?? null);
      setValue(
        'comment' as Path<TSchema>,
        (translatedDamage?.comment || data.comment) ?? '',
      );
      setValue('active' as Path<TSchema>, data.active);
    }
  }, [setValue, data, translatedDamage]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const damageErrorMessages = existingDamage?.data?.errorMessages;

    if (damageErrorMessages?.includes(FieldError.NAME_WITH_SEVERITY_EXISTS)) {
      setError('name' as Path<TSchema>, {
        message: t('damageNameWithSeverityAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <form onSubmit={handleSubmit}>
      <Card
        title={
          <TitleSection text={t('theDamage')} defaultIconClassName="ki-badge" />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <FormField
          text={t('common:icon')}
          containerClassName="mb-2 max-md:mb-0"
          textWrapperClassName="mt-3 max-md:mt-0"
        >
          <Avatar
            outerContainerClassName="max-md:w-full"
            containerClassName="max-md:mx-auto"
            imageUrl={data?.icon}
            onChange={(url) =>
              setValue?.(
                'icon' as Path<TSchema>,
                url as PathValue<TSchema, Path<TSchema>>,
              )
            }
          />
        </FormField>
        <FormField text={t('damageName')} required textWrapperClassName="mt-3">
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Input
              {...(register && register('name' as Path<TSchema>))}
              errorMessage={errors && (errors.name?.message as string)}
              placeholder={t('damageName')}
              type="text"
            />
          </div>
        </FormField>
        <FormField
          text={t('common:severity')}
          required
          textWrapperClassName="mt-3"
        >
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Select
              {...(register &&
                register('severity' as Path<TSchema>, {
                  valueAsNumber: true,
                }))}
              errorMessage={errors && (errors.severity?.message as string)}
              placeholder={t('chooseSeverity')}
              options={[
                { label: '1', value: 1 },
                { label: '2', value: 2 },
                { label: '3', value: 3 },
              ]}
            />
          </div>
        </FormField>
        <FormField text={t('common:comment')} textWrapperClassName="mt-3">
          <div className="flex flex-1 flex-wrap items-start gap-5">
            <Textarea
              {...(register && register('comment' as Path<TSchema>))}
              errorMessage={errors && (errors.comment?.message as string)}
              placeholder={t('common:comment')}
            />
          </div>
        </FormField>
        <FormField text={t('common:activeStatus')} textWrapperClassName="mt-3">
          <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
            <Switch
              {...(register && register('active' as Path<TSchema>))}
              errorMessage={errors && (errors.active?.message as string)}
              checked={data ? activeValue : true}
            />
          </div>
        </FormField>
      </Card>
    </form>
  );
};
